import { useEffect, useState } from 'react'
import { useForm, Controller, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  Card,
  CardBody,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  VStack,
  HStack,
  Text,
  Grid,
  Select,
  Spinner,
} from '@chakra-ui/react'
import { createFixedExpensesSchema, updateFixedExpensesSchema, type CreateFixedExpensesFormData, type UpdateFixedExpensesFormData } from '@/lib/validations/auth'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useThemeColors } from '@/hooks/useThemeColors'
import { SearchableSelect } from '@/components/common/SearchableSelect'

interface FixedExpensesFormProps {
  fixedExpensesId?: number
  onSuccess: () => void
  onCancel: () => void
}

export function FixedExpensesForm({ fixedExpensesId, onSuccess, onCancel }: FixedExpensesFormProps) {
  const colors = useThemeColors()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const isEditMode = !!fixedExpensesId
  const { showError, showSuccess } = useToastNotification()

  const [shippingLineOptions, setShippingLineOption] = useState<{ id: number; name: string }[]>([])
  const [cypaOptions, setCypaOption] = useState<{ id: number; name: string }[]>([])
  type ContainerSize = '20ft' | '40ft' | '45ft'
  const containerSizeOptions: ContainerSize[] = ['20ft', '40ft', '45ft']

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm<CreateFixedExpensesFormData | UpdateFixedExpensesFormData>({
    resolver: zodResolver(isEditMode ? updateFixedExpensesSchema : createFixedExpensesSchema),
    defaultValues: {
        shipping_line_id: undefined,
        cypa_id_from: undefined,
        cypa_id_to: undefined,
        container_size: '20ft',
        docs_fee: 0,
        stack_run: 0,
        expenses: 0,
        online_booking_fee: null,
    },
  })

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      try {
        let record: Record<string, unknown> = {}
        if (isEditMode && fixedExpensesId) {
          const response = await apiClient.get(`/fixed-expenses/${fixedExpensesId}`)
          record = (response.data?.data || response.data || {}) as Record<string, unknown>
        }

        const [shippingLineDataResponse, cypaDataResponse] = await Promise.all([
          apiClient.get('/shipping-lines'),
          apiClient.get('/container-yards/yard-list', { params: { is_active: 1 } }),
        ])

        let shippingLineData = (shippingLineDataResponse.data?.data || shippingLineDataResponse.data || []) as Array<{ id: number; name: string }>
        let cypaData = (cypaDataResponse.data?.data || cypaDataResponse.data || []) as Array<{ id: number; name: string }>

        if (isEditMode && Object.keys(record).length > 0) {
          const shippingLineId = record.shipping_line_id as number | undefined
          const shippingLine = record.shipping_line as { id?: number; name?: string } | undefined
          if (shippingLineId != null && !shippingLineData.some((item) => item.id === shippingLineId)) {
            shippingLineData = [{ id: shippingLineId, name: shippingLine?.name ?? 'Unknown' }, ...shippingLineData]
          }

          const cypaFromId = record.cypa_id_from as number | undefined
          const cypaFrom =
            (record.cypa_from as { id?: number; name?: string } | undefined) ??
            (record.cypa_id_from_data as { id?: number; name?: string } | undefined)
          if (cypaFromId != null && !cypaData.some((item) => item.id === cypaFromId)) {
            cypaData = [{ id: cypaFromId, name: cypaFrom?.name ?? 'Unknown' }, ...cypaData]
          }

          const cypaToId = record.cypa_id_to as number | undefined
          const cypaTo =
            (record.cypa_to as { id?: number; name?: string } | undefined) ??
            (record.cypa_id_to_data as { id?: number; name?: string } | undefined)
          if (cypaToId != null && !cypaData.some((item) => item.id === cypaToId)) {
            cypaData = [{ id: cypaToId, name: cypaTo?.name ?? 'Unknown' }, ...cypaData]
          }
        }

        setShippingLineOption(shippingLineData)
        setCypaOption(cypaData)

        if (isEditMode && Object.keys(record).length > 0) {
          reset({
            shipping_line_id: (record.shipping_line_id ?? (record.shipping_line as { id?: number })?.id) as number | undefined,
            cypa_id_from: (record.cypa_id_from ?? (record.cypa_from as { id?: number })?.id) as number | undefined,
            cypa_id_to: (record.cypa_id_to ?? (record.cypa_to as { id?: number })?.id) as number | undefined,
            container_size: (record.container_size as ContainerSize | undefined) ?? '20ft',
            docs_fee: (record.docs_fee as number | undefined) ?? 0,
            stack_run: (record.stack_run as number | undefined) ?? 0,
            expenses: (record.expenses as number | undefined) ?? 0,
            online_booking_fee: (record.online_booking_fee as number | null | undefined) ?? null,
          })
        }
      } catch (error: any) {
        showError(error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [isEditMode, fixedExpensesId, reset, showError])
  

  const onSubmit = async (data: CreateFixedExpensesFormData | UpdateFixedExpensesFormData) => {
    setIsSubmitting(true)
    try {
      const payload = {
        shipping_line_id: data.shipping_line_id,
        cypa_id_from: data.cypa_id_from,
        cypa_id_to: data.cypa_id_to,
        container_size: data.container_size,
        docs_fee: data.docs_fee,
        stack_run: data.stack_run,
        expenses: data.expenses,
        online_booking_fee: data.online_booking_fee ?? 0,
        total_expenses: data.docs_fee + data.stack_run + data.expenses + (data.online_booking_fee ?? 0),
    }

    //   // 🔎 FRONTEND DUPLICATE CHECK
    // const checkResponse = await apiClient.get('/fixed-expenses', {
    //   params: {
    //     shipping_line_id: data.shipping_line_id,
    //     cypa_id_from: data.cypa_id_from,
    //     cypa_id_to: data.cypa_id_to,
    //     container_size: data.container_size,
    //   },
    // })

    // const existing = checkResponse.data?.data || []

    // // If creating → block if any match exists
    // if (!isEditMode && existing.length > 0) {
    //   showError(
    //     'Duplicate Entry, This Shipping Line already has this combination.'
    //   )
    //   setIsSubmitting(false)
    //   return
    // }

    // // If editing → exclude current record
    // if (isEditMode) {
    //   const duplicate = existing.find(
    //     (item: any) => item.id !== fixedExpensesId
    //   )
    //   if (duplicate) {
    //     showError(
    //       'Duplicate Entry, This Shipping Line already has this Pickup and Delivery combination.'
    //     )
    //     setIsSubmitting(false)
    //     return
    //   }
    // }

    if (isEditMode) {
      await apiClient.put(`/fixed-expenses/${fixedExpensesId}`, payload)
      showSuccess('Success', 'Fixed Expense updated successfully')
    } else {
      await apiClient.post('/fixed-expenses', payload)
      showSuccess('Success', 'Fixed Expense created successfully')
    }

      onSuccess?.()
    } catch (error: any) {
      showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }
  

  const selectedFromCypa = useWatch({
    control,
    name: 'cypa_id_from',
  })
  const selectedToCypa = useWatch({
    control,
    name: 'cypa_id_to',
  })

  if (isLoading) {
    return (
      <Card>
        <CardBody>
          <VStack spacing={4} align="center" py={6}>
            <Spinner color="blue.500" />
            <Text color={colors.text}>Loading form...</Text>
          </VStack>
        </CardBody>
      </Card>
    )
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={4} align="stretch">
        {/* Name Field */}
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Shipping Line Name <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.shipping_line_id}>
            <Controller
              name="shipping_line_id"
              control={control}
              render={({ field }) => (
                <SearchableSelect
                  options={shippingLineOptions}
                  optionsUrl="/shipping-lines"
                  value={field.value ?? null}
                  onChange={(value) => field.onChange(value !== null ? Number(value) : undefined)}
                  placeholder="Select shipping line"
                  isInvalid={!!errors.shipping_line_id}
                />
              )}
            />
            <FormErrorMessage>
                {errors.shipping_line_id?.message}
            </FormErrorMessage>
          </FormControl>
        </Grid>


        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Pickup Point <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.cypa_id_from}>
            <Controller
              name="cypa_id_from"
              control={control}
              render={({ field }) => (
                <SearchableSelect
                  options={cypaOptions}
                  optionsUrl="/container-yards/yard-list"
                  optionsParams={{ is_active: 1 }}
                  excludeOptionId={selectedToCypa ?? null}
                  value={field.value ?? null}
                  onChange={(value) => field.onChange(value !== null ? Number(value) : undefined)}
                  placeholder="Select the Pickup Point"
                  isInvalid={!!errors.cypa_id_from}
                />
              )}
            />
            <FormErrorMessage>
                {errors.cypa_id_from?.message}
            </FormErrorMessage>
          </FormControl>
        </Grid>


        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Deliver To<Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.cypa_id_to}>
            <Controller
              name="cypa_id_to"
              control={control}
              render={({ field }) => (
                <SearchableSelect
                  options={cypaOptions}
                  optionsUrl="/container-yards/yard-list"
                  optionsParams={{ is_active: 1 }}
                  excludeOptionId={selectedFromCypa ?? null}
                  value={field.value ?? null}
                  onChange={(value) => field.onChange(value !== null ? Number(value) : undefined)}
                  placeholder="Select the Delivery Point"
                  isInvalid={!!errors.cypa_id_to}
                />
              )}
            />
            <FormErrorMessage>
                {errors.cypa_id_to?.message}
            </FormErrorMessage>
          </FormControl>
        </Grid>


        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Container Size<Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.container_size}>
            <Select placeholder="Select the Container Size"
                {...register('container_size')}
            >
                {containerSizeOptions.map((size) => (<option key={size} value={size}> {size} </option> ))}
            </Select>

            <FormErrorMessage>
                {errors.container_size?.message}
            </FormErrorMessage>
          </FormControl>
        </Grid>

        
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Docs Fee <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.docs_fee}>
            <Input type="number" {...register('docs_fee', { valueAsNumber: true })} placeholder="Input the Docs Fee price"/>
            <FormErrorMessage>{errors.docs_fee?.message}</FormErrorMessage>
          </FormControl>
        </Grid>


        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Stack Run <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.stack_run}>
            <Input type="number" {...register('stack_run', { valueAsNumber: true })} placeholder="Input the Stack run price"/>
            <FormErrorMessage>{errors.stack_run?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Expenses <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.expenses}>
           <Input type="number" {...register('expenses', { valueAsNumber: true })} placeholder="Input the Expense price"/>
            <FormErrorMessage>{errors.expenses?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Online Booking Fee
          </FormLabel>
          <FormControl isInvalid={!!errors.online_booking_fee}>
            <Input
              type="number"
              {...register('online_booking_fee', { valueAsNumber: true })}
              placeholder="0.00"
              step="0.01"
              min="0"
            />
            <FormErrorMessage>{errors.online_booking_fee?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        {/* Action Buttons */}
        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button
              variant="outline"
              onClick={onCancel}
              isDisabled={isSubmitting}
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            colorScheme="blue"
            isLoading={isSubmitting}
            loadingText={isEditMode ? 'Saving...' : 'Creating...'}
          >
            {isEditMode ? 'Save' : 'Create Fixed Expense'}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}